"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  ConversationsService,
  type ConversationHistoryPage,
  type ConversationWithMessages,
} from "@/generated";
import { PublicConversationService } from "@/services/public/Conversation";
import { SpotToast } from "../SpotToast";
import type { NoxusUIMessage } from "./noxus-message-types";
import { withStoredFeedback } from "./utils";

type PublicMetadata = {
  token: string;
  deployment_id?: string;
  auth_token?: string;
};

const cursorOf = (history: ConversationHistoryPage | null | undefined) =>
  history?.has_more ? (history.before ?? null) : null;

const messagesOf = (page: ConversationWithMessages) =>
  withStoredFeedback(page.vercel_messages as NoxusUIMessage[], page.messages);

// A page of earlier messages, and the cursor it was fetched with: it belongs
// above the list only while the list still starts there.
type EarlierPage = {
  before: string;
  next: string | null;
  messages: NoxusUIMessage[];
};

export function useEarlierMessages({
  history,
  groupId,
  conversationId,
  assistantId,
  publicMetadata,
  setMessages,
}: {
  history: ConversationHistoryPage | null | undefined;
  groupId: string;
  conversationId: string;
  assistantId: string;
  publicMetadata?: PublicMetadata;
  setMessages: (
    update: (messages: NoxusUIMessage[]) => NoxusUIMessage[]
  ) => void;
}) {
  const t = useTranslations("widget.status");
  const [cursor, setCursor] = useState<string | null>(() => cursorOf(history));
  // Read after an await, where the state above is stale.
  const cursorRef = useRef(cursor);
  const moveCursor = useCallback((next: string | null) => {
    cursorRef.current = next;
    setCursor(next);
  }, []);

  const { mutateAsync, isPending } = useMutation({
    // No `before`: the newest page.
    mutationFn: async (before?: string): Promise<ConversationWithMessages> => {
      if (publicMetadata) {
        return PublicConversationService.getConversation({
          spotGroupId: groupId,
          conversationId,
          assistantId,
          token: publicMetadata.token,
          deploymentId: publicMetadata.deployment_id,
          authToken: publicMetadata.auth_token,
          paged: true,
          before,
        });
      }
      const response = await ConversationsService.getConversation({
        path: { group_id: groupId, conversation_id: conversationId },
        query: { paged: true, before },
      });
      return response.data;
    },
  });

  // Resolves to the page above the list, or null when the request failed. The
  // caller adds it with `prepend`, so it can measure the scroll position right
  // before the list grows.
  const fetchEarlier = useCallback(async (): Promise<EarlierPage | null> => {
    const before = cursorRef.current;
    if (!before) return null;
    try {
      const page = await mutateAsync(before);
      return {
        before,
        next: cursorOf(page.history),
        messages: messagesOf(page),
      };
    } catch {
      SpotToast.error(t("loadPreviousFailed"));
      return null;
    }
  }, [mutateAsync, t]);

  // False when the list moved since the page was asked for (a compaction
  // swapped in the newest page): the page no longer sits above it.
  const prepend = useCallback(
    (page: EarlierPage) => {
      if (cursorRef.current !== page.before) return false;
      moveCursor(page.next);
      setMessages((current) => [...page.messages, ...current]);
      return true;
    },
    [moveCursor, setMessages]
  );

  // The newest page replaces the list unless something came after `lastId`
  // (none for an empty list) or `unchanged` says another request started
  // while it loaded: a tool or approval continuation keeps writing to that
  // same reply.
  const showNewest = useCallback(
    async (lastId: string | undefined, unchanged: () => boolean) => {
      let page: ConversationWithMessages;
      try {
        page = await mutateAsync(undefined);
      } catch {
        return;
      }
      if (!unchanged()) return;
      let replaced = false;
      setMessages((current) => {
        if (current.at(-1)?.id !== lastId) return current;
        replaced = true;
        return messagesOf(page);
      });
      if (replaced) moveCursor(cursorOf(page.history));
    },
    [moveCursor, mutateAsync, setMessages]
  );

  return useMemo(
    () => ({
      hasEarlier: cursor !== null,
      isLoading: isPending,
      fetchEarlier,
      prepend,
      showNewest,
    }),
    [cursor, isPending, fetchEarlier, prepend, showNewest]
  );
}
